import {
  type ReplaceSupplierIndustryRow,
  type SupplierIndustry,
  type SupplierIndustryKind,
  NONE_OF_THE_ABOVE
} from '@tandemone/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useReducer } from 'react'

import { extractErrorMessage } from '@/api/extract-error'

import { replaceSupplierIndustries } from './api'
import { SUPPLIER_INDUSTRY_OPTIONS } from './enum-labels'
import { createEnumPicker } from './enum-picker'
import {
  type IndustriesFormAction,
  type IndustriesFormState,
  type IndustryRowState,
  IndustriesFormContext
} from './industries-context'
import { invalidateOnboarding } from './invalidate-onboarding'

const NONE: SupplierIndustryKind = NONE_OF_THE_ABOVE

const KINDS = Object.keys(SUPPLIER_INDUSTRY_OPTIONS) as SupplierIndustryKind[]
export const NONE_INDUSTRY: SupplierIndustryKind = NONE

const picker = createEnumPicker<
  SupplierIndustryKind,
  SupplierIndustry,
  IndustryRowState
>({
  kinds: KINDS,
  noneKind: NONE,
  empty: () => ({ selected: false, yearsInBusiness: 0 }),
  hydrate: row => ({
    kind: row.industry,
    row: { selected: true, yearsInBusiness: row.yearsInBusiness }
  }),
  isIncomplete: r => r.yearsInBusiness === 0
})

export const REAL_INDUSTRY_KINDS = picker.realKinds

function reducer(
  state: IndustriesFormState,
  action: IndustriesFormAction
): IndustriesFormState {
  switch (action.type) {
    case 'setYears':
      return {
        ...state,
        [action.kind]: {
          ...state[action.kind],
          yearsInBusiness: action.years
        }
      }
    default:
      return picker.reduceCommon(state, action)
  }
}

export function IndustriesFormProvider({
  children,
  initial,
  onSuccess
}: {
  children: ReactNode
  initial: SupplierIndustry[]
  onSuccess: () => void
}) {
  const queryClient = useQueryClient()
  const [state, dispatch] = useReducer(reducer, picker.initialState(initial))
  const derived = picker.derive(state)

  const mutation = useMutation({
    mutationFn: (industries: ReplaceSupplierIndustryRow[]) =>
      replaceSupplierIndustries({ industries }),
    onSuccess: async () => {
      await invalidateOnboarding(queryClient, 'supplier-industries')
      onSuccess()
    },
    onError: failure =>
      dispatch({ type: 'submitError', message: extractErrorMessage(failure) })
  })

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!derived.isValid || mutation.isPending) return
    dispatch({ type: 'submitError', message: null })

    const payload: ReplaceSupplierIndustryRow[] = []
    if (derived.noneSelected) {
      payload.push({ industry: NONE, yearsInBusiness: 0 })
    } else {
      for (const k of REAL_INDUSTRY_KINDS) {
        const row = state[k]
        if (!row.selected) continue
        payload.push({ industry: k, yearsInBusiness: row.yearsInBusiness })
      }
    }
    mutation.mutate(payload)
  }

  return (
    <IndustriesFormContext.Provider
      value={{
        state,
        derived,
        dispatch,
        isSubmitting: mutation.isPending,
        handleSubmit
      }}
    >
      {children}
    </IndustriesFormContext.Provider>
  )
}
